import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { isProblemError } from '../../api/client';
import {
  getGetMeQueryKey,
  useAcceptTerms,
  useGetCurrentTerms,
} from '../../api/generated/identity/identity';
import type { TermsDocument, TermsSummary } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import styles from './terms.module.css';

/** Enquanto a versao publicada for esta, o texto e marcador sem valor juridico. */
const PLACEHOLDER_VERSION = '0.0.0-placeholder';

export interface TermsPageProps {
  readonly pendingTerms: readonly TermsSummary[];
}

export function TermsPage({ pendingTerms }: TermsPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentTerms = useGetCurrentTerms();
  const accept = useAcceptTerms();

  const pendingIds = new Set(pendingTerms.map((summary) => summary.id));
  const documents = (currentTerms.data ?? []).filter((document) => pendingIds.has(document.id));

  const handleAccept = (document: TermsDocument): void => {
    // Envia o documento **efetivamente apresentado**, que carrega o locale em
    // que o texto foi lido. Quando houve queda para pt-BR, e o pt-BR que vai.
    accept.mutate(
      { data: { termsDocumentId: document.id } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
        },
      },
    );
  };

  const errorMessage = ((): string | undefined => {
    if (!accept.isError) return undefined;
    if (!isProblemError(accept.error)) return t('error.unexpected');
    if (accept.error.status === 409) return t('terms.alreadyAccepted');
    if (accept.error.status === 404) return t('terms.notFound');
    return t('error.unexpected');
  })();

  return (
    <main className={styles.screen}>
      <div className={styles.container}>
        <h1 className={styles.title}>{t('terms.title')}</h1>
        <p className={styles.intro}>{t('terms.intro')}</p>

        {currentTerms.isPending ? <p>{t('app.loading')}</p> : null}
        {currentTerms.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
        {errorMessage ? <Alert tone="error">{errorMessage}</Alert> : null}

        {documents.map((document) => (
          <section key={document.id} className={styles.document} aria-labelledby={document.id}>
            <h2 id={document.id} className={styles.documentTitle}>
              {t(`terms.kind.${document.kind}`)}
            </h2>
            <p className={styles.version}>{t('terms.version', { version: document.version })}</p>

            {/*
              Apresentar um marcador provisorio como termo definitivo produziria
              consentimento invalido. Enquanto a versao for a placeholder, o
              aviso vem antes do texto, e nao em nota de rodape.
            */}
            {document.version === PLACEHOLDER_VERSION ? (
              <Alert tone="warning">{t('terms.placeholderWarning')}</Alert>
            ) : null}

            <p className={styles.body}>{document.body}</p>

            <Button
              variant="primary"
              loading={accept.isPending}
              onClick={() => handleAccept(document)}
            >
              {accept.isPending ? t('terms.accepting') : t('terms.accept')}
            </Button>
          </section>
        ))}
      </div>
    </main>
  );
}
